/* eslint-disable no-param-reassign */
import { createSlice, SerializedError } from '@reduxjs/toolkit'
import { SubscriberUserInfo } from 'pages/dashboard/main/LiveReportConfigModal/types'
import { FetchErrorResponse } from 'api'
import { getLiveReportId, getLiveReportInfo, updateLiveReportStatus } from './asyncThunks'
import { Status } from '../types'

export interface LiveReportConfigSlice {
  error: string | SerializedError | null | undefined
  isOpen: boolean
  savingInfo: {
    saveStatus: Status
    statusCode: any
    errorInfo: string
    failedSubscribers: string[] | []
  }
  reportId: {
    status: Status
    data: string | null
  }
  reportInfo: {
    status: Status
    reportKey: string
    reportEnabled: boolean
    subscriberInfos: SubscriberUserInfo[]
    anonymous: boolean
  }
}

export const initialState: Readonly<LiveReportConfigSlice> = {
  error: null,
  isOpen: false,
  savingInfo: {
    saveStatus: 'idle',
    statusCode: null,
    errorInfo: '',
    failedSubscribers: []
  },
  reportId: {
    status: 'idle',
    data: null
  },
  reportInfo: {
    status: 'idle',
    reportKey: '',
    reportEnabled: false,
    subscriberInfos: [],
    anonymous: false
  }
}

const liveReportConfigSlice = createSlice({
  name: 'liveReportConfig',
  initialState,
  reducers: {
    openLiveReportConfigModal: (state) => {
      state.isOpen = true
    },
    closeLiveReportConfigModal: (state) => {
      state.isOpen = false
    },
    resetSavingInfo: (state) => {
      state.savingInfo.saveStatus = 'idle'
      state.savingInfo.statusCode = null
      state.savingInfo.errorInfo = ''
      state.savingInfo.failedSubscribers = []
    }
  },
  extraReducers: (builder) =>
    builder
      .addCase(getLiveReportId.pending, (state) => {
        state.reportId.status = 'loading'
      })
      .addCase(getLiveReportId.fulfilled, (state, action) => {
        state.reportId.status = 'succeeded'
        state.reportId.data = action.payload
      })
      .addCase(getLiveReportId.rejected, (state) => {
        state.reportId.status = 'failed'
      })
      .addCase(getLiveReportInfo.pending, (state) => {
        state.reportInfo.status = 'loading'
      })
      .addCase(getLiveReportInfo.fulfilled, (state, action) => {
        state.reportInfo.status = 'succeeded'
        state.reportInfo.reportKey = action.payload.liveReportKey
        state.reportInfo.reportEnabled = action.payload.liveReportEnabled
        state.reportInfo.subscriberInfos = action.payload.liveReportSubscribers
        state.reportInfo.anonymous = action.payload.anonymous
      })
      .addCase(getLiveReportInfo.rejected, (state) => {
        state.reportInfo.status = 'failed'
      })
      .addCase(updateLiveReportStatus.pending, (state) => {
        state.savingInfo.saveStatus = 'loading'
      })
      .addCase(updateLiveReportStatus.fulfilled, (state, action) => {
        state.savingInfo.saveStatus = 'succeeded'
        state.reportInfo.reportKey = action.payload.liveReportKey
        state.reportInfo.reportEnabled = action.payload.liveReportEnabled
        state.reportInfo.subscriberInfos = action.payload.liveReportSubscribers
        state.reportInfo.anonymous = action.payload.anonymous
        state.reportId.data = action.payload.liveReportId
      })
      .addCase(updateLiveReportStatus.rejected, (state, action) => {
        state.savingInfo.saveStatus = 'failed'
        if (action?.payload) {
          const errorPayload = action.payload as FetchErrorResponse
          state.savingInfo.errorInfo = errorPayload.error!
          state.savingInfo.statusCode = errorPayload.status!
          state.savingInfo.failedSubscribers = errorPayload.info!
        }
      })
})

export const {
  openLiveReportConfigModal,
  closeLiveReportConfigModal,
  resetSavingInfo
} = liveReportConfigSlice.actions
export default liveReportConfigSlice.reducer
